'use client';

import {
  Children,
  isValidElement,
  useId,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { ChevronLeft, ChevronRight } from 'react-feather';
import { Image, publicAssetUrl } from '@wilkins-software/redwood-utils';
import { ArticleImageLightbox } from './article-image-lightbox.component';

export type ArticleGallerySlideVariant = 'photo' | 'screenshot';

export type ArticleGallerySlideProps = {
  src: string;
  alt: string;
  caption?: string;
  variant?: ArticleGallerySlideVariant;
};

/**
 * Marker component for MDX. Props are collected by {@link ArticleImageGallery}.
 */
export function ArticleGallerySlide(_props: ArticleGallerySlideProps) {
  return null;
}

type ArticleImageGalleryProps = {
  children: ReactNode;
  className?: string;
  /** Optional caption shown under the gallery when the active slide has none. */
  caption?: string;
};

const ARTICLE_GALLERY_SIZES =
  '(min-width: 896px) 896px, (min-width: 640px) 90vw, 100vw';

function resolvePublicImageSrc(src: string): string {
  return publicAssetUrl(src.replace(/^\//, ''));
}

function extractGallerySlides(children: ReactNode): ArticleGallerySlideProps[] {
  return Children.toArray(children).flatMap((child) => {
    if (!isValidElement(child)) {
      return [];
    }

    const slideProps = child.props as Partial<ArticleGallerySlideProps>;
    if (
      typeof slideProps.src !== 'string' ||
      typeof slideProps.alt !== 'string'
    ) {
      return [];
    }

    return [
      {
        src: slideProps.src,
        alt: slideProps.alt,
        caption: slideProps.caption,
        variant: slideProps.variant ?? 'photo',
      },
    ];
  });
}

function GallerySlideImage({
  slide,
  isActive,
  onOpenLightbox,
}: {
  slide: ArticleGallerySlideProps;
  isActive: boolean;
  onOpenLightbox: () => void;
}) {
  const imageSrc = resolvePublicImageSrc(slide.src);
  const isScreenshot = slide.variant === 'screenshot';

  return (
    <div
      className={[
        'flex h-full min-h-0 w-full items-center justify-center bg-slate-50',
        isScreenshot ? 'p-4 sm:p-6' : 'p-2 sm:p-3',
      ].join(' ')}
    >
      {/*
        Omit width/height so Unpic uses fullWidth (width-only CF transforms).
        fill + fit:cover would crop photos and screenshots in the letterbox frame.
        Image click opens lightbox; carousel chevrons/dots stay sibling controls.
      */}
      <button
        type="button"
        onClick={onOpenLightbox}
        tabIndex={isActive ? 0 : -1}
        aria-label={
          isActive ? `View full-size image: ${slide.alt}` : undefined
        }
        aria-hidden={!isActive}
        className="group relative block h-full max-h-full w-full cursor-zoom-in rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
      >
        <Image
          src={imageSrc}
          alt={isActive ? slide.alt : ''}
          sizes={ARTICLE_GALLERY_SIZES}
          className="max-h-full w-full object-contain"
        />
        <span className="pointer-events-none absolute inset-0 rounded-lg ring-0 transition-[box-shadow] group-hover:ring-1 group-hover:ring-inset group-hover:ring-slate-900/10" />
      </button>
    </div>
  );
}

export function ArticleImageGallery({
  children,
  className,
  caption,
}: ArticleImageGalleryProps) {
  const slides = extractGallerySlides(children);
  const galleryLabelId = useId();
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const slideCount = slides.length;
  const safeActiveSlideIndex =
    slideCount === 0 ? 0 : Math.min(activeSlideIndex, slideCount - 1);
  const activeSlide = slides[safeActiveSlideIndex];
  const activeCaption = activeSlide?.caption ?? caption;

  const lightboxItems = slides.map((slide) => ({
    src: resolvePublicImageSrc(slide.src),
    alt: slide.alt,
    caption: slide.caption ?? caption,
  }));

  // Deliberately not wrapped in useCallback. The React Compiler could not
  // preserve the hand-written memoization here and skipped the whole component
  // because of it; letting the compiler own the memoization gets both callbacks
  // — and everything else in this component — memoized instead.
  const goToPreviousSlide = () => {
    setActiveSlideIndex((currentIndex) => {
      if (slideCount === 0) {
        return 0;
      }
      const clampedIndex = Math.min(currentIndex, slideCount - 1);
      return clampedIndex === 0 ? slideCount - 1 : clampedIndex - 1;
    });
  };

  const goToNextSlide = () => {
    setActiveSlideIndex((currentIndex) => {
      if (slideCount === 0) {
        return 0;
      }
      const clampedIndex = Math.min(currentIndex, slideCount - 1);
      return clampedIndex === slideCount - 1 ? 0 : clampedIndex + 1;
    });
  };

  if (slideCount === 0 || !activeSlide) {
    return null;
  }

  function handleGalleryKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (isLightboxOpen) {
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goToPreviousSlide();
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goToNextSlide();
    }
  }

  return (
    <figure
      className={[
        'clear-both my-8 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div
        role="region"
        aria-roledescription="carousel"
        aria-labelledby={galleryLabelId}
        tabIndex={0}
        onKeyDown={handleGalleryKeyDown}
        className="relative outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
      >
        <p id={galleryLabelId} className="sr-only">
          Image gallery, slide {safeActiveSlideIndex + 1} of {slideCount}
        </p>

        <div className="relative aspect-[16/10] max-h-[26rem] w-full overflow-hidden bg-slate-50">
          {slides.map((slide, slideIndex) => {
            const isActive = slideIndex === safeActiveSlideIndex;

            return (
              <div
                key={`${slide.src}-${slideIndex}`}
                aria-hidden={!isActive}
                className={[
                  'absolute inset-0 transition-opacity duration-300 ease-out motion-reduce:transition-none',
                  isActive
                    ? 'z-[1] opacity-100'
                    : 'pointer-events-none z-0 opacity-0',
                ].join(' ')}
              >
                <GallerySlideImage
                  slide={slide}
                  isActive={isActive}
                  onOpenLightbox={() => setIsLightboxOpen(true)}
                />
              </div>
            );
          })}
        </div>

        {slideCount > 1 ? (
          <>
            <button
              type="button"
              onClick={goToPreviousSlide}
              aria-label="Previous image"
              className="absolute top-1/2 left-3 z-[2] flex size-9 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200/80 bg-white/90 text-slate-700 shadow-sm backdrop-blur-sm transition-colors hover:bg-white hover:text-primary"
            >
              <ChevronLeft className="size-5" aria-hidden />
            </button>
            <button
              type="button"
              onClick={goToNextSlide}
              aria-label="Next image"
              className="absolute top-1/2 right-3 z-[2] flex size-9 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200/80 bg-white/90 text-slate-700 shadow-sm backdrop-blur-sm transition-colors hover:bg-white hover:text-primary"
            >
              <ChevronRight className="size-5" aria-hidden />
            </button>

            <div
              className="absolute bottom-3 left-1/2 z-[2] flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-slate-900/50 px-2.5 py-1.5 backdrop-blur-sm"
              role="tablist"
              aria-label="Gallery slides"
            >
              {slides.map((slide, slideIndex) => {
                const isActive = slideIndex === safeActiveSlideIndex;
                return (
                  <button
                    key={`${slide.src}-${slideIndex}`}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-label={`Show image ${slideIndex + 1}: ${slide.alt}`}
                    onClick={() => setActiveSlideIndex(slideIndex)}
                    className={[
                      'size-2 rounded-full transition-colors motion-reduce:transition-none',
                      isActive
                        ? 'bg-white'
                        : 'bg-white/45 hover:bg-white/75',
                    ].join(' ')}
                  />
                );
              })}
            </div>
          </>
        ) : null}
      </div>

      <figcaption className="border-t border-slate-100 px-4 py-3 text-sm leading-5 text-slate-600">
        <span className="sr-only" aria-live="polite">
          {activeSlide.alt}
        </span>
        {activeCaption ? (
          <span>{activeCaption}</span>
        ) : (
          <span className="text-slate-500">{activeSlide.alt}</span>
        )}
        {slideCount > 1 ? (
          <span className="mt-1 block text-xs font-medium tracking-wide text-slate-400">
            {safeActiveSlideIndex + 1} / {slideCount}
          </span>
        ) : null}
      </figcaption>

      <ArticleImageLightbox
        open={isLightboxOpen}
        onClose={() => setIsLightboxOpen(false)}
        items={lightboxItems}
        activeIndex={safeActiveSlideIndex}
        onActiveIndexChange={setActiveSlideIndex}
      />
    </figure>
  );
}
